import React, { ReactElement, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { MultipleAccountsPopover } from './MultipleAccountsPopover';
import { FilesPopover } from './FilesPopover';

import { useAuth } from 'src/hooks';

import { ActivityLogListType } from '../types/activityLogType';

dayjs.extend(utc);

type ActivityLogItemType = {
  activityLog: ActivityLogListType;
  selectedToggleValue: string;
};

function ActivityLogItem({
  activityLog,
  selectedToggleValue,
}: ActivityLogItemType): ReactElement {
  const { isExternalUser } = useAuth();

  const totalAccounts = useMemo(() => {
    const value = activityLog?.AccountCIDPID || '';
    return value.split(',').filter(Boolean).length;
  }, [activityLog?.AccountCIDPID]);

  const getIdLabel = () => {
    if (selectedToggleValue === 'personRepository') return 'Person ID';
    if (selectedToggleValue === 'personAlerts') return 'Alert Number';
    if (selectedToggleValue === 'hospitalForm') return 'Submission Number';
    if (selectedToggleValue === 'callDocuments' || selectedToggleValue === '')
      return 'File(s)';
    return '';
  };

  const getIdValue = () => {
    if (selectedToggleValue === 'personRepository') return activityLog.PersonId;
    if (selectedToggleValue === 'personAlerts')
      return activityLog.PersonAlertId;
    if (selectedToggleValue === 'callDocuments' || selectedToggleValue === '')
      return activityLog.ReportFileName;
    if (selectedToggleValue === 'hospitalForm') return activityLog.FormId;
    return '';
  };

  return (
    <div className="relative flex flex-nowrap rounded bg-white shadow focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-blue-600 dark:bg-neutral-800 dark:focus-visible:outline-blue-400">
      <div className="flex flex-grow flex-wrap px-4 py-2">
        {/* Activity Date */}
        <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[20%]">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            Activity Date
          </label>
          <p className="text-xs font-bold tracking-wide text-black dark:text-white">
            {dayjs(activityLog?.CreatedOn).format('MM/DD/YYYY H:mm:ss')}
          </p>
        </div>
        {/* Account / CID / PID */}
        {!isExternalUser && selectedToggleValue === 'callDocuments' ? (
          <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[10%]">
            <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
              Account
            </label>
            <p
              className={`text-xs font-bold tracking-wide text-black dark:text-white ${
                totalAccounts > 1 ? 'flex' : ''
              }`}
            >
              {totalAccounts === 1
                ? activityLog?.AccountCIDPID
                : activityLog?.AccountCIDPID !== null && (
                    <MultipleAccountsPopover activityLog={activityLog} />
                  )}
            </p>
          </div>
        ) : (
          !isExternalUser && (
            <>
              <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[10%]">
                <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                  Account CID - PID
                </label>
                <p className="text-xs font-bold tracking-wide text-black dark:text-white">
                  {activityLog?.AccountCIDPID}
                </p>
              </div>
            </>
          )
        )}

        {isExternalUser && selectedToggleValue === '' && (
          <>
            <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[10%]">
              <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                CID
              </label>
              <p className="text-xs font-bold tracking-wide text-black dark:text-white">
                {activityLog?.CID}
              </p>
            </div>

            <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[10%]">
              <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                PID
              </label>
              <p className="text-xs font-bold tracking-wide text-black dark:text-white">
                {activityLog?.PID}
              </p>
            </div>
          </>
        )}
        {/*  */}
        <div
          className={`w-full p-2 sm:w-1/2 md:w-1/3 ${
            selectedToggleValue === 'callDocuments' ||
            selectedToggleValue === ''
              ? 'lg:w-[30%]'
              : 'lg:w-[15%]'
          }`}
        >
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            {getIdLabel()}
          </label>
          {!isExternalUser &&
          (selectedToggleValue === 'callDocuments' ||
            selectedToggleValue === '') ? (
            <p className="prevent-text-breakout flex text-xs font-bold tracking-wide text-black dark:text-white">
              {(activityLog?.Activity !== 'CSV Download' &&
                activityLog?.TotalCallDocuments === 1) ||
              activityLog?.TotalCallDocuments === null ? (
                getIdValue()
              ) : (
                <FilesPopover activityLog={activityLog} />
              )}
            </p>
          ) : (
            <p className="prevent-text-breakout text-xs font-bold tracking-wide text-black dark:text-white">
              {getIdValue()}
            </p>
          )}
        </div>
        {/* Activity */}
        <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[15%]">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            Activity
          </label>
          <p className="text-xs font-bold tracking-wide text-black dark:text-white">
            {activityLog?.Activity}
          </p>
        </div>
        {/* User */}
        <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[15%]">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            User
          </label>
          <p className="prevent-text-breakout text-xs font-bold tracking-wide text-black dark:text-white">
            {activityLog?.UserName}
          </p>
        </div>

        {isExternalUser && (
          <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[15%]">
            <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
              Source
            </label>
            <p className="prevent-text-breakout text-xs font-bold tracking-wide text-black dark:text-white">
              {!activityLog?.IsADUser
                ? 'Customer'
                : process.env.REACT_APP_THEME === 'lfl'
                ? 'Lines for Life'
                : 'Protocall'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export { ActivityLogItem };
